import { useTheme } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setExtraProductData,
  setLanguageID,
  setLanguageWords,
  updateDefaultLanguage,
  updateEventDates,
  updateEventOrStdTicketTypesData,
  updateEventsOrStandardTickets,
  updateSelectedDate,
  updateStdDates,
} from "../store/slice/Ticketing.slice";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FetchWidgetConfigDetails,
  GetGuestDetailsByKey,
  getExtraProductDetails,
  getLanguageDetails,
  getWidgetSettings,
  getWidgetTitleandInfo,
} from "../api/HorecaWidget.service";
import {
  setLanguageLoaded,
  updateButtonText,
  updateCombinationLang,
  updateCss,
  updateGuestInfo,
  updateLanguageData,
  updateWidgetConfig,
  updateWidgetSetting,
} from "../store/slice/widgetSetting.slice";
import customParseFormat from "dayjs/plugin/customParseFormat";
import dayjs from "dayjs";
import { setCombinationList } from "../store/slice/Activity.slice";
import { getCombinationList } from "../api/activity.service";
import {
  fetchEventOrStandardTicketsTypes,
  fetchEventTicketsDates,
  fetchStandardTicketsDates,
  getEventsOrStandardTickets,
} from "../api/ticketing.service";
import { extractTimes } from "../utils/utils";
dayjs?.extend(customParseFormat); // Extend for custom date format parsing

let loadSettings = null;
let widgetIdSettings = null;

const useTicketingWidget = () => {
  const [loader, setLoader] = React.useState(false);
  const [isLoadingDates, setIsLoadingDates] = React.useState(false);
  const [widgetLoading, setWidgetLoading] = useState(false);
  const [combinationLoading, setCombinationLoading] = useState(false);
  const [langLoading, setLangLoading] = useState(false);
  const [eventStdTicketLoader, setEventorStdTicketLoader] = useState(false);
  const theme = useTheme();
  const {
    totalCount,
    EventDates,
    selectedDate,
    reservType,
    adult,
    child,
    kid,
    baby,
    toddler,
    isBackToHoreca,
    defaultLanguage,
    isOpenWidget,
    SelectedEventOrStdResType,
  } = useSelector((store) => store.Ticketing);
  const {
    key,
    widgetConfig,
    languageLoaded,
    guestId,
    resType,
    widgetSetupId,
    buttonText,
    floating,
  } = useSelector((store) => store?.widgetSettings);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openWidget, setOpenWidget] = useState(
    location?.state?.openWidgetScreen ?? isOpenWidget
  );
  const getWidgetPositionStyle = (position, btn) => {
    switch (position) {
      case "Top":
        return { top: "0", left: "0", transform: "translate(0, 0)" };
      case "Top-Left":
        return { top: "20px", left: "0px", transform: "translate(0, 0)" };
      case "Top-Middle":
        return { top: "0", left: "50%", transform: "translateX(-50%)" };
      case "Top-Right":
        return { top: "0", right: "20px", transform: "translate(0, 0)" };
      case "Middle-Left":
        return { top: "50%", left: "0px", transform: "translateY(-50%)" };
      case "Middle-Center":
        return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
      case "Middle-Right":
        return { top: "50%", right: "20px", transform: "translateY(-50%)" };
      case "Bottom-Left":
        return { bottom: "20px", left: "0px", transform: "translate(0, 0)" };
      case "Bottom-Middle":
        return { bottom: "20px", left: "50%", transform: "translateX(-50%)" };
      case "Bottom-Right":
        return { bottom: "0", right: "0", transform: "translate(0, 0)" };
      default:
        if (btn) {
          return { bottom: "0", right: "0", transform: "translate(0, 0)" };
        } else {
          return {
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          };
        }
    }
  };
  const fetchLanguageDetails = async () => {
    setLangLoading(true);
    try {
      const response = await getLanguageDetails(key);
      if (!response || response.status !== 200) {
        console.error("Error fetching language details:", response);
        return;
      }
      const langParamFromURL = queryParams.get("lang");
      const isValidLang =
        langParamFromURL &&
        !["null", "undefined", ""].includes(langParamFromURL.trim());
      let selectedLang = isValidLang ? langParamFromURL : defaultLanguage;
      const languageList = response?.data || [];

      // If no lang in URL, use the defaultLanguage from API response
      if (!selectedLang) {
        selectedLang = languageList.find(
          (item) => item?.defaultLanguage
        )?.langcode;
      }

      // Update Redux store
      if (selectedLang) {
        dispatch(updateDefaultLanguage(selectedLang));
      }
      dispatch(updateLanguageData(languageList));
      dispatch(setLanguageLoaded(true));

      // Store widget configuration
      localStorage.setItem(
        "widget",
        JSON.stringify({
          id: widgetSetupId,
          key,
          lang: selectedLang,
          resType,
        })
      );
    } catch (error) {
      console.error("Error fetching language details widget:", error?.message);
    } finally {
      setLangLoading(false);
    }
  };
  const fetchWidgetSettings = async (language) => {
    try {
      const response = await getWidgetSettings(language, key);
      if (response?.status !== 200 || !response?.data) {
        throw new Error(
          "Failed to fetch widget settings. Please try again later."
        );
      }
      loadSettings = response?.data;
      dispatch(updateWidgetSetting(response?.data));
    } catch (error) {
      const errorMsg =
        error?.message ||
        "An unexpected error occurred while fetching widget settings.";
      console.error("Error fetching widget settings:", errorMsg);
    }
  };
  const fetchWidgetTitleandInfo = async (widgetSetupId, language) => {
    try {
      const response = await getWidgetTitleandInfo(widgetSetupId, language);
      if (response?.status != 200 || !response?.data) {
        throw new Error(
          "Failed to fetch widget title and info. Please try again later."
        );
      }
      widgetIdSettings = response?.data;
    } catch (error) {
      console.error("Error fetching widget settings:", error?.message);
    }
  };
  const fetchEventOrStandardTickets = async () => {
    try {
      setLoader(true); 
  
      const response = await getEventsOrStandardTickets(
        key,
        defaultLanguage,
        true,
        true,
        widgetSetupId
      );
  
      dispatch(updateEventsOrStandardTickets(response));
  
    } catch (error) {
      console.error("Error fetching events or standard tickets:", error);
    } finally {
      setLoader(false); 
    }
  };
  
  const fetchStdDatesForEvent = async () => {
    try {
      setIsLoadingDates(true); 
  
      const response = await fetchStandardTicketsDates(key);
  
      dispatch(updateStdDates(response));
  
    } catch (error) {
      console.error("Error fetching standard dates for event:", error);
    } finally {
      setIsLoadingDates(false); 
    }
  };
  
  const fetchEventDates = async (id) => {
    try {
      const response = await fetchEventTicketsDates(id);
      dispatch(updateEventDates(response)); // Assuming 'data' contains standard dates data. Update the state with this data.
    } catch (error) {
      console.error("Error fetching standard dates for event:", error);
    }
  };

  const fetchEventOrStdTicketTypes = async () => {
    setEventorStdTicketLoader(true);
    try {
      const response = await fetchEventOrStandardTicketsTypes(
        key,
        defaultLanguage,
        reservType?.eventTicketId,
        reservType?.isStandardTicket
          ? selectedDate
          : selectedDate?.availableDate,
        reservType?.isStandardTicket
      );
      dispatch(updateEventOrStdTicketTypesData(response)); // Assuming 'data' contains event or standard ticket types data. Update the state with this data.
      setEventorStdTicketLoader(false);
    } catch (error) {
      console.error("Error fetching event or standard ticket types:", error);
      setEventorStdTicketLoader(false);
    }
  };
  const fetchExtraTicketProducts = async () => {
    try {
      const response = await getExtraProductDetails(
        SelectedEventOrStdResType?.idreservationtype,
        defaultLanguage
      );
      if (response?.status == 200) {
        dispatch(setExtraProductData(response?.data));
      }
    } catch (error) {
      console.log("error extra product", error?.message);
    }
  };

  const fetchWidgetSettingsDetails = async (lang) => {
    try {
      setWidgetLoading(true);
      const response = await FetchWidgetConfigDetails(
        key,
        widgetSetupId,
        lang,
        guestId
      );
      if (response?.status != 200 || !response?.data) {
        throw new Error(
          "Failed to fetch widget settings details. Please try again later."
        );
      }
      const result = response?.data?.basicLanguages?.reduce((acc, item) => {
        const tag = item.tag.replace(/{|}/g, "").replace(/\s+/g, "");
        const data = item.widgetTranslateLanguages[0]?.name;
        acc[tag] = data; // Assign data to the key acc[tag]
        return acc;
      }, {});
      dispatch(updateCss(response?.data?.settings?.cssGroup))
      dispatch(updateWidgetConfig(response?.data));
      dispatch(updateButtonText(response?.data?.fields?.reservationButtonText));
      dispatch(setLanguageWords(result));
      dispatch(
        updateCombinationLang({
          bookReservation: result?.bookReservation,
          selectCategory: result?.selectCategory,
        })
      );
      setWidgetLoading(false);
    } catch (error) {
      console.error("Error fetching widget settings details:", error);
      setWidgetLoading(false);
    }
  };

  const fetchGuestDetailsByToken = async () => {
    try {
      const response = await GetGuestDetailsByKey(
        widgetConfig?.token,
        key,
        guestId
      );
      if (!response) {
        throw new Error(
          "Failed to fetch guest details. Please try again later."
        );
      }
      dispatch(updateGuestInfo(response));
    } catch (error) {
      console.error("Error fetching guest details by token:", error);
    }
  };

  useEffect(() => {
    if (widgetConfig?.token) {
      fetchGuestDetailsByToken();
    }
  }, [widgetConfig]);

  useEffect(() => {
    if (
      Object.keys(SelectedEventOrStdResType)?.length > 0 &&
      SelectedEventOrStdResType?.idreservationtype
    ) {
      fetchExtraTicketProducts();
    }
  }, [SelectedEventOrStdResType]);

  const handleClose = () => {
    setOpenWidget(false);
  };
  useEffect(() => {
    if (key && defaultLanguage) fetchEventOrStandardTickets();
  }, [defaultLanguage]);
  useEffect(() => {  
     fetchLanguageDetails();  
  }, []);
  useEffect(() => {
    if (defaultLanguage && defaultLanguage.trim() !== "" && !isBackToHoreca) {
      fetchWidgetSettings(defaultLanguage);
    }
  }, [defaultLanguage, isBackToHoreca]);
  useEffect(() => {
    if (defaultLanguage && defaultLanguage.trim() !== "") {
      // Checks if defaultLanguage is a non-empty string
      fetchWidgetSettingsDetails(defaultLanguage);
    }
  }, [defaultLanguage]);
  useEffect(() => {
    if (defaultLanguage?.trim() != "" && !isBackToHoreca)
      fetchWidgetTitleandInfo(widgetSetupId, defaultLanguage);
  }, [defaultLanguage, isBackToHoreca]);
  useEffect(() => {
    if (Object.keys(reservType)?.length > 0) {
      if (reservType?.isStandardTicket) {
        fetchStdDatesForEvent();
      } else {
        fetchEventDates(reservType?.eventTicketId);
      }
    }
  }, [reservType]);
  useEffect(() => {
    if (Object.keys(reservType)?.length > 0 && selectedDate)
      fetchEventOrStdTicketTypes();
  }, [selectedDate]);
  const fetchCombinations = async () => {
    setCombinationLoading(true);
    dispatch(setCombinationList([]));
    const{fromTime,toTime} =extractTimes(reservType?.eventDateAndTime)
    try {
      const data = reservType?.isStandardTicket
        ? selectedDate
        : selectedDate?.availableDate;
      const parsedDate = dayjs(data, "YYYY-MM-DD", true);
      if (!parsedDate.isValid()) {
        throw new Error("Invalid date");
      }
      const payload = {
        key,
        idreservationtype: Number(SelectedEventOrStdResType?.idreservationtype),
        ArrivalDate: parsedDate.format("YYYY-MM-DD"),
        ArrivalTime: fromTime,
        DepatureTime: toTime,
        Field1: Number(adult),
        Field2: Number(child),
        Field3: Number(kid),
        Field4: Number(toddler),
        Field5: Number(baby),
        LanguageCode: defaultLanguage,
        NoOfGuest: Number(totalCount),
      };

      const response = await getCombinationList(payload);
      dispatch(setCombinationList(response));
    } catch (error) {
      console.error("fetchCombinations error:", error);
    } finally {
      setCombinationLoading(false); 
    }
  };

  useEffect(() => {
    if (
      Object.keys(SelectedEventOrStdResType)?.length > 0 &&
      SelectedEventOrStdResType?.idreservationtype && widgetConfig?.settings?.hasCombination
    ) {
      fetchCombinations();
    }
  }, [SelectedEventOrStdResType]);

  useEffect(() => {
    if (EventDates?.length == 1) {
      dispatch(updateSelectedDate(EventDates[0]));
    }
  }, [EventDates]);

  const send = (
    frameHeight,
    frameWidth,
    top,
    right,
    bottom,
    left,
    floating,
    redirectUrl
  ) => {
    window.parent.postMessage(
      {
        reservationIframeHeight: frameHeight,
        reservationIframeWidth: frameWidth,
        reservationIframePositionTop: top,
        reservationIframePositionRight: right,
        reservationIframePositionBottom: bottom,
        reservationIframePositionLeft: left,
        reservationIframePositionFloating: floating,
        redirectUrl: redirectUrl,
      },
      "*"
    );
  };
  // get the height of the widget
  const getHeight = () => {
    setTimeout(() => {
      let rootHeight = document.getElementsByClassName("MuiBox-root")[1];
      let popupHeight = document.getElementsByClassName("MuiPaper-root")[0];
      let frameHeight = 200;
      if (rootHeight) {
        if (rootHeight.clientHeight > frameHeight) {
          if (rootHeight.clientWidth < 700) {
            frameHeight = rootHeight.clientHeight + 100;
          } else {
            frameHeight = rootHeight.clientHeight + 20;
          }
        }
        if (popupHeight) {
          if (frameHeight < 520) {
            frameHeight = 520;
          }
        }
      }
      send(
        frameHeight + "px",
        "100%",
        "",
        "0px",
        "0px",
        "",
        widgetConfig?.settings?.floatButtonActive
      );
    }, 100);
  };
  // get the height of the MuiBox-root after loading the full page
  useEffect(() => {
    if (!widgetConfig?.settings?.floatButtonActive || floating === "relative") {
      getHeight();
    }
  }, []);

  return {
    combinationLoading,
    totalCount,
    EventDates,
    selectedDate,
    reservType,
    adult,
    child,
    kid,
    baby,
    toddler,
    isBackToHoreca,
    defaultLanguage,
    isOpenWidget,
    SelectedEventOrStdResType,
    key,
    widgetConfig,
    languageLoaded,
    guestId,
    resType,
    location,
    navigate,
    dispatch,
    openWidget,
    setOpenWidget,
    getWidgetPositionStyle,
    send,
    widgetLoading,
    setWidgetLoading,
    theme,
    handleClose,
    buttonText,
    langLoading,
    eventStdTicketLoader,
    loader
    
  };
};

export default useTicketingWidget;
